// M09 - EducaFolderSidebar
//
// Sidebar de carpetas planas bajo EducaFiles/. La raiz es seleccionable
// pero no borrable. Crear carpeta abre un Modal con Input. Borrar usa
// Popconfirm y el backend rechaza si la carpeta tiene ficheros dentro.

import React, { useState } from "react";
import {
  Modal,
  Input,
  Popconfirm,
  Button,
  Tooltip,
  message,
  Empty,
} from "antd";
import {
  FolderOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  DeleteOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import styles from "./EducaFolderSidebar.module.scss";

export function EducaFolderSidebar({
  folders,
  activeFolder,
  onSelect,
  loading,
  rootKey,
  onCreate,
  onDelete,
}) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      message.warning("Escribe un nombre");
      return;
    }
    try {
      setSubmitting(true);
      await onCreate(name);
      setCreating(false);
      setNewName("");
    } catch (e) {
      message.error(e?.body?.message || e?.message || "Error creando carpeta");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <span className={styles.title}>Carpetas</span>
        <Tooltip title="Nueva carpeta">
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => setCreating(true)}
          />
        </Tooltip>
      </div>

      <ul className={styles.list}>
        <li>
          <button
            type="button"
            className={
              activeFolder === rootKey
                ? `${styles.item} ${styles.itemActive}`
                : styles.item
            }
            onClick={() => onSelect(rootKey)}
          >
            <HomeOutlined className={styles.icon} />
            <span className={styles.itemName}>EducaFiles (raiz)</span>
          </button>
        </li>

        {loading ? <li className={styles.muted}>Cargando...</li> : null}

        {!loading && folders.length === 0 ? (
          <li className={styles.emptyState}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin carpetas"
              styles={{ image: { height: 36 } }}
            />
          </li>
        ) : null}

        {folders.map((f) => {
          const isActive = activeFolder === f.name;
          return (
            <li key={f.name} className={styles.row}>
              <button
                type="button"
                className={
                  isActive ? `${styles.item} ${styles.itemActive}` : styles.item
                }
                onClick={() => onSelect(f.name)}
              >
                {isActive ? (
                  <FolderOpenOutlined className={styles.icon} />
                ) : (
                  <FolderOutlined className={styles.icon} />
                )}
                <span className={styles.itemName}>{f.name}</span>
              </button>
              <Popconfirm
                title="Borrar carpeta"
                description="Solo se puede borrar si esta vacia."
                okText="Borrar"
                okButtonProps={{ danger: true }}
                cancelText="Cancelar"
                onConfirm={() => onDelete(f.name)}
              >
                <Tooltip title="Borrar">
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    className={styles.deleteBtn}
                  />
                </Tooltip>
              </Popconfirm>
            </li>
          );
        })}
      </ul>

      <Modal
        title="Nueva carpeta"
        open={creating}
        onCancel={() => {
          setCreating(false);
          setNewName("");
        }}
        onOk={handleCreate}
        confirmLoading={submitting}
        okText="Crear"
        cancelText="Cancelar"
        destroyOnClose
      >
        <p style={{ marginTop: 0, color: "rgba(0,0,0,0.6)" }}>
          La carpeta se creara directamente bajo EducaFiles/.
        </p>
        <Input
          placeholder="Ej. Logos, Campana2026, VideosCursos"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={63}
          onPressEnter={handleCreate}
          autoFocus
        />
      </Modal>
    </div>
  );
}
